/** Static copy for footer legal / support pages */

export const LEGAL_PAGES = {
  faqs: {
    title: 'Frequently Asked Questions',
    eyebrow: 'Support',
    sections: [
      {
        heading: 'Booking & quotes',
        body: [
          'You can request a quote through our Contact page or WhatsApp. Share origin, destination, cargo type, weight or CBM, and preferred service (air, sea FCL/LCL, or door-to-door).',
          'We typically respond within one business day with lane options and estimated transit times.',
        ],
      },
      {
        heading: 'Tracking',
        body: [
          'Use the Track Shipment page with your HBL or tracking number. Milestones update as cargo moves through our network.',
          'If your number is not found, contact us with your receipt or collection number and we will assist.',
        ],
      },
      {
        heading: 'Customs & documentation',
        body: [
          'We assist with export documentation from Malaysia and can coordinate import clearance at destination where our partners operate.',
          'For Sri Lanka personal effects and baggage rules, see our dedicated customs guide.',
        ],
        links: [{ label: 'Sri Lanka customs & baggage guide', to: '/sri-lanka-customs-baggage' }],
      },
      {
        heading: 'Payments',
        body: [
          'Payment terms depend on service type and account status. Receipts are issued for collections at our warehouse.',
          'For billing questions on your account, sign in to your profile or contact our finance team.',
        ],
      },
    ],
  },
  terms: {
    title: 'Terms & Conditions',
    eyebrow: 'Legal',
    lastUpdated: 'July 2026',
    sections: [
      {
        heading: '1. Services',
        body: [
          'Jet Shipping Cargo provides freight forwarding, consolidation, warehousing, and related logistics services. Specific terms for each shipment are confirmed in writing (quote, receipt, or HBL) before cargo is accepted.',
        ],
      },
      {
        heading: '2. Customer responsibilities',
        body: [
          'You must provide accurate cargo descriptions, weights, dimensions, and values. Prohibited or undeclared goods may be refused, held, or reported to authorities.',
          'You are responsible for compliance with export, import, and transit regulations at origin and destination.',
        ],
      },
      {
        heading: '3. Liability',
        body: [
          'Liability is limited as permitted under applicable conventions and our standard forwarding terms unless additional insurance is purchased in writing.',
          'We are not liable for delays caused by customs, weather, carrier schedules, or events outside our reasonable control.',
        ],
      },
      {
        heading: '4. Payments & cancellation',
        body: [
          'Charges are due as stated on invoices or receipts. Storage or demurrage may apply for uncollected cargo.',
          'Cancellation fees may apply after booking or collection depending on work already performed.',
        ],
      },
      {
        heading: '5. Governing law',
        body: [
          'These terms are governed by the laws of Malaysia. Disputes shall be subject to the courts of Kuala Lumpur unless otherwise agreed in writing.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    eyebrow: 'Legal',
    lastUpdated: 'July 2026',
    sections: [
      {
        heading: 'Information we collect',
        body: [
          'We collect information you provide on forms (name, email, phone, company, shipment details), account registration data, and communications with our team.',
          'We may collect technical data such as IP address and browser type for security and analytics.',
        ],
      },
      {
        heading: 'How we use information',
        body: [
          'To provide quotes, process shipments, send status updates, respond to enquiries, and improve our services.',
          'With your consent, we may send newsletters or promotional updates. You can unsubscribe at any time.',
        ],
      },
      {
        heading: 'Sharing',
        body: [
          'We share data with carriers, customs brokers, and partners only as needed to move your cargo and comply with law.',
          'We do not sell personal data to third parties.',
        ],
      },
      {
        heading: 'Retention & security',
        body: [
          'We retain records as required for operations, accounting, and legal compliance.',
          'We use reasonable technical and organisational measures to protect your data.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'You may request access, correction, or deletion of personal data where applicable law allows. Contact us at support@jetshippingandlogistic.com.',
        ],
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    eyebrow: 'Legal',
    lastUpdated: 'July 2026',
    sections: [
      {
        heading: 'General',
        body: [
          'Freight charges cover handling, documentation, and carrier costs. Refunds are considered case-by-case when service was not provided as agreed.',
        ],
      },
      {
        heading: 'Eligible situations',
        body: [
          'Duplicate payment or billing error verified by our finance team.',
          'Service cancellation before cargo is handed to the carrier, minus any costs already incurred.',
          'Documented loss attributable to our error, subject to liability limits in our Terms & Conditions.',
        ],
      },
      {
        heading: 'Non-refundable items',
        body: [
          'Carrier charges already paid, customs duties, storage, and third-party fees are generally non-refundable.',
          'Delays outside our control (weather, port congestion, customs inspection) do not automatically qualify for refund.',
        ],
      },
      {
        heading: 'How to request',
        body: [
          'Email support@jetshippingandlogistic.com with your receipt or HBL number, payment reference, and reason for the request. We aim to respond within 5 business days.',
        ],
        links: [{ label: 'Contact us', to: '/contact' }],
      },
    ],
  },
  careers: {
    title: 'Careers',
    eyebrow: 'Join us',
    sections: [
      {
        heading: 'Work with us',
        body: [
          'Jet Shipping Cargo is growing across air, sea, and land freight from Kuala Lumpur. We look for people who care about clear communication and reliable execution.',
        ],
      },
      {
        heading: 'Open roles',
        body: [
          'We periodically hire operations coordinators, warehouse staff, and customer service representatives. Specific openings are shared on our social channels and by referral.',
        ],
      },
      {
        heading: 'How to apply',
        body: [
          'Send your CV and cover letter to jetshippingandlogistic@gmail.com with the role you are interested in.',
          'Include any logistics, customs, or freight forwarding experience.',
        ],
        links: [{ label: 'Contact us', to: '/contact' }],
      },
    ],
  },
  help: {
    title: 'Help Center',
    eyebrow: 'Support',
    sections: [
      {
        heading: 'Get help quickly',
        body: [
          'Track a shipment, read FAQs, or message us on WhatsApp for urgent lane questions.',
        ],
        links: [
          { label: 'Track shipment', to: '/tracking' },
          { label: 'FAQs', to: '/faqs' },
          { label: 'Contact form', to: '/contact' },
        ],
      },
      {
        heading: 'Office hours',
        body: [
          'Monday – Friday: 8:00 AM – 6:00 PM',
          'Saturday: 9:00 AM – 4:00 PM',
          'Sunday: Closed',
          'WhatsApp messages may be answered outside hours when staff are available.',
        ],
      },
      {
        heading: 'Shipping guide',
        body: [
          'See our Services page for air, sea, warehousing, and door-to-door options, or use the CBM calculator to estimate volume.',
        ],
        links: [
          { label: 'Our services', to: '/services' },
          { label: 'CBM calculator', to: '/cbm-calculator' },
        ],
      },
    ],
  },
  sitemap: {
    title: 'Sitemap',
    eyebrow: 'Navigation',
    sections: [
      {
        heading: 'Main pages',
        links: [
          { label: 'Home', to: '/' },
          { label: 'About', to: '/about' },
          { label: 'Services', to: '/services' },
          { label: 'Pricing', to: '/pricing' },
          { label: 'Track shipment', to: '/tracking' },
          { label: 'Contact', to: '/contact' },
          { label: 'News', to: '/news' },
          { label: 'Leave a review', to: '/review' },
        ],
      },
      {
        heading: 'Account',
        links: [
          { label: 'Register', to: '/register' },
          { label: 'Login', to: '/login' },
          { label: 'Profile', to: '/profile' },
        ],
      },
      {
        heading: 'Legal & support',
        links: [
          { label: 'FAQs', to: '/faqs' },
          { label: 'Help center', to: '/help' },
          { label: 'Terms & conditions', to: '/terms' },
          { label: 'Privacy policy', to: '/privacy' },
          { label: 'Refund policy', to: '/refund-policy' },
          { label: 'Careers', to: '/careers' },
        ],
      },
    ],
  },
};

export const LEGAL_SLUGS = Object.keys(LEGAL_PAGES);
